import { Box, Grid, Typography, Card, CardMedia, CircularProgress, Avatar } from '@mui/material';
import React from 'react';
import { styled } from '@mui/system';
import AddDeviceModal from './AddDeviceModal';
import DeviceCard from './DeviceCard';
import PairDeviceCard from './PairDeviceCard';
import { getDevices, getDexcomAuthUrl, deviceIntegrationStatus } from '../../../utils/api';
import useStore from '../../../Store/Store';
import { isEmpty } from 'lodash';
import { useQuery } from 'react-query';
import AppleStoreIcon from '../../../images/appStore.svg';
import GoogleStoreIcon from '../../../images/playStore.svg';
import abbotFreeStyle from '../../../images/freestyle.png';
import dexcom from '../../../images/dexcom.png';
import IntegrationSetupModal from './IntegrationSetupModal';
import { Button } from '@mui/material';
import SnackbarComponent from '../../../components/Snackbar';
import canLogo from '../../../images/canLogo.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SOURCE_LIST = [
  {
    id: 1,
    title: 'Download CAN CARE',
    img: canLogo,
    download: true,
    icon: AppleStoreIcon,
    link: 'https://apps.apple.com/us/app/can-co/id6737693177',
  },
  {
    id: 2,
    title: 'Download CAN CARE',
    img: canLogo,
    download: true,
    icon: GoogleStoreIcon,
    link: 'https://play.google.com/store/apps/details?id=co.can.canCare&pli=1',
  },
  {
    id: 3,
    title: 'Freestyle Libre',
    img: abbotFreeStyle,
    download: false,
    icon: abbotFreeStyle,
    link: null,
    isConnected: false,
  },
  {
    id: 4,
    title: 'Dexcom',
    img: dexcom,
    download: false,
    icon: dexcom,
    link: null,
    isConnected: false,
  },
];

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  padding: 15,
  marginBottom: 2,
  boxShadow: 'none',
  flexWrap: 'wrap',
  gap: '10px',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    padding: '12px',
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '16px',
  overflow: 'hidden',
  border: '1px solid rgba(229, 229, 234, 1)',
  objectFit: 'contain',
  backgroundColor: '#fff',
  [theme.breakpoints.down('sm')]: {
    width: '50px',
    height: '50px',
  },
}));

const Integrations = () => {
  const user = useStore((state) => state.user);
  const [deviceModal, setAddDeviceModal] = React.useState(false);
  const [integrationSetupModal, setIntegrationSetupModal] = React.useState(false);
  const [integrationType, setIntegrationType] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const {
    data: integrationStatusData,
    isLoading,
    refetch: refetchIntegrationStatus,
  } = useQuery(['integrationStatus'], () => deviceIntegrationStatus({ userId: user?._id }));

  const handleClick = async (item) => {
    if (item.download) {
      window.open(item.link);
    } else {
      if (item.id === 3) {
        // on id 3 there is free style integration
        setIntegrationSetupModal(true);
        setIntegrationType(item.title);
      }
      if (item.id === 4) {
        try {
          const res = await getDexcomAuthUrl({ userId: user?._id });
          window.location.href = res.data;
        } catch (err) {
          setError(err.response.data.message);
          setOpenSnackbar(true);
        }
      }
    }
  };
  const { data: devices, refetch } = useQuery(['getDevices'], () => getDevices({ userId: user?._id || user?.id }), {
    retry: false,
    cacheTime: 0,
  });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={6}>
          {isEmpty(devices?.data[0]) ? (
            <PairDeviceCard setAddDeviceModal={() => setAddDeviceModal(true)} />
          ) : (
            <DeviceCard device={devices?.data || null} user={user} />
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: { xs: 2, md: 0 }, // Add margin top on mobile
            }}
          >
            {SOURCE_LIST.map((item) => {
              let isConnected = false;
              if (integrationStatusData) {
                if (item.id === 3) isConnected = integrationStatusData.data.freestyle;
                if (item.id === 4) isConnected = integrationStatusData.data.dexcom;
              }
              return (
                <StyledCard key={item.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      flexWrap: 'wrap',
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                    }}
                  >
                    <StyledCardMedia component="img" image={item.img} alt={item.title} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "'Roboto', Helvetica",
                        fontWeight: 500,
                        color: 'rgba(29, 29, 31, 1)',
                        textAlign: { xs: 'center', sm: 'left' },
                        fontSize: { xs: '16px', sm: '20px' },
                      }}
                    >
                      {item?.title}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: { xs: 'center', sm: 'flex-end' },
                    }}
                  >
                    {item.id === 1 || item.id === 2 ? (
                      <img
                        src={item?.icon}
                        alt={item?.title}
                        style={{ width: '200px', height: '50px', cursor: 'pointer' }}
                        onClick={() => handleClick(item)}
                      />
                    ) : isConnected ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        px={2}
                        py={0.5}
                        sx={{
                          background: '#f6fbf3',
                          borderRadius: '999px',
                          width: 'fit-content',
                          minWidth: 0,
                          height: 40,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: '#a3d86d',
                            width: 24,
                            height: 24,
                          }}
                        >
                          <CheckCircleIcon sx={{ color: 'white', width: 16, height: 16 }} />
                        </Avatar>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: 'Roboto, Helvetica',
                            fontWeight: 400,
                            fontSize: { xs: '12px', sm: '14px' },
                            color: '#222',
                          }}
                        >
                          Connected
                        </Typography>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => !isLoading && handleClick(item)}
                        disabled={isLoading}
                        sx={{
                          backgroundColor: '#fff',
                          borderRadius: '24px',
                          boxShadow: 'none',
                          color: '#3b9b8f',
                          borderColor: '#3b9b8f',
                          '&:hover': {
                            backgroundColor: '#3b9b8f',
                            borderColor: '#3b9b8f',
                            color: '#fff',
                          },
                        }}
                        endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                      >
                        Connect
                      </Button>
                    )}
                  </Box>
                </StyledCard>
              );
            })}
          </Box>
        </Grid>
      </Grid>
      <SnackbarComponent
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        message="Error"
        severity="error"
        body={error}
        autoHide={5000}
      />
      <AddDeviceModal show={deviceModal} onClose={() => setAddDeviceModal(false)} refetch={refetch} />
      <IntegrationSetupModal
        integrationType={integrationType}
        open={integrationSetupModal}
        onClose={() => setIntegrationSetupModal(false)}
        user={user}
        refetchIntegrationStatus={refetchIntegrationStatus}
      />
    </>
  );
};

export default Integrations;
